export default function StJosephTravelTimingSection() {
  return (
    <section className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Regional Travel Expertise</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Travel &amp; Passport Timing for St. Joseph Travelers
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Understanding how St. Joseph-area travelers move helps us anticipate the passport urgencies that arise here. From agriculture industry travel to MWSU study-abroad seasons, Northwest Missouri has travel patterns that create distinct passport timing pressures.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12">
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-flight-takeoff-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">MCI — Kansas City International Airport</h3>
                <p className="text-gray-400 text-xs">~1 hour south via I-29</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Kansas City International Airport (MCI) is the primary international gateway for St. Joseph travelers — just under an hour south on I-29. MCI offers nonstop service to major domestic hubs and international connections through Chicago, Dallas, Atlanta, and New York. For St. Joseph agriculture exporters, business travelers, and families, MCI is the departure point for virtually all international travel, making passport validity a critical planning element.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Check your passport expiration 6 months before any MCI international departure. Many countries require 6 months validity beyond your return date — and agriculture exporters often book flights with shorter lead times than the average leisure traveler.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-seedling-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Agriculture &amp; Livestock Industry Travel</h3>
                <p className="text-gray-400 text-xs">Missouri beef cattle, grain export, international trade</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Northwest Missouri's agriculture industry — particularly beef cattle production — generates significant international travel to Canada, Mexico, Australia, and South America for livestock purchasing, export negotiations, and trade shows. Cattle buyers and agriculture export professionals often work on commodity-market timelines that can require international travel within days of a deal closing. A passport that expired during the COVID-era travel pause creates an immediate crisis in these situations.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Agriculture professionals traveling to Canada for livestock purchases need a valid passport — not just an enhanced driver's license for air travel. Keep your passport renewed well ahead of buying and selling seasons.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-graduation-cap-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">MWSU Study Abroad &amp; Student Travel</h3>
                <p className="text-gray-400 text-xs">Missouri Western State University</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Missouri Western State University's study-abroad programs send students to destinations across Europe, Latin America, and Asia. Many MWSU students are first-time passport applicants who receive program acceptance in October or February — when their passport application deadline conflicts with standard government processing timelines of 10–16 weeks. First-time applications require an in-person acceptance clerk visit at a St. Joseph post office, which we guide students through step by step.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> MWSU students: apply for your passport as soon as you receive program acceptance — not after orientation. First-time applications require an in-person clerk visit first, which adds time even with expedited processing.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-hospital-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Mosaic Life Care &amp; Healthcare Travel</h3>
                <p className="text-gray-400 text-xs">Regional medical hub — Buchanan County</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Mosaic Life Care is Northwest Missouri's primary medical center, drawing healthcare professionals who travel internationally for specialized training, conferences, and collaborative programs. International medical conferences in Europe, Canada, and South America require current passports that may have been overlooked since the last renewal cycle. We work with healthcare professionals on compressed conference registration timelines.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Conference registration deadlines are months before the event — but many healthcare professionals don't check their passport validity until booking flights. Check now, before a conference registration locks in your travel date.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-[#1A365D] rounded-lg p-7 flex flex-col md:flex-row items-center gap-6">
          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
            <i className="ri-history-line text-[#F6C344] text-2xl"></i>
          </div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="font-bold text-white text-base mb-2">Pony Express Heritage &amp; Tourism Travel</h3>
            <p className="text-white/60 text-sm leading-relaxed">
              St. Joseph is the birthplace of the Pony Express — a heritage that draws history-focused travelers and international tourists. The Pony Express National Museum and St. Joseph's Victorian architectural district attract visitors who sometimes find themselves facing travel document issues far from home. We serve travelers throughout Northwest Missouri, not just St. Joseph residents.
            </p>
          </div>
          <a
            href="https://fastpassportcenter.com/checkout/"
            className="bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-6 py-3 rounded-md transition-all whitespace-nowrap flex-shrink-0"
          >
            Get Started <i className="ri-arrow-right-line ml-1"></i>
          </a>
        </div>
      </div>
    </section>
  );
}